import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { env } from '../constants/env'
import { AuthState } from '../App.d'
import { Auth } from 'aws-amplify'

const session = async () => await Auth.currentSession()

export const baseAxiosInstance = axios.create({
	baseURL: env.apiBaseUrl,
	headers: {},
})

baseAxiosInstance.interceptors.request.use(async (config) => {
	config.headers.Authorization = (await session()).getIdToken().getJwtToken()
	return config
})

const getUrl = (endpoint: string): string => `${env.apiBaseUrl}/${endpoint}`

export type RequestStatus = 'Loading' | 'Ready' | 'Error'

export const getPagedData = async (
	endpoint: string,
	limit: number,
	updatePage: (data: any, pageStatus: RequestStatus) => void,
	authState?: AuthState
): Promise<AxiosResponse<any>> => {
	let offset = 0
	let finished = false
	let status: RequestStatus = 'Loading'
	let result: any

	while (!finished) {
		result = await get(`${endpoint}${endpoint.endsWith('&') ? '' : '?'}limit=${limit}&offset=${offset}`, authState)

		if (!result.data || result.status !== 200) {
			status = 'Error'
			finished = true
		} else {
			offset += limit

			if (result.data.resultsLength < limit) {
				status = 'Ready'
				finished = true
			}
		}
		updatePage(result.data, status)
	}
	return {
		...result,
		data: [],
	}
}

export const get = async (endpoint: string, authState?: AuthState): Promise<AxiosResponse<any>> => {
	return new Promise<AxiosResponse<any>>((resolve, reject) => {
		baseAxiosInstance
			.get(getUrl(endpoint))
			.catch((error: any) => {
				if (error.response && error.response.status === 401) {
					window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname) + window.location.hash
				}
				reject(error)
			})
			.then((value: AxiosResponse<any> | void) => {
				resolve(value as any)
			})
	})
}

export const post = (endpoint: string, body: any, authState?: AuthState) => baseAxiosInstance.post(getUrl(endpoint), body)
export const publicPost = (endpoint: string, body: any) => {
	return axios.post(`${env.apiBaseUrl}/${endpoint}`, body)
}

export const put = (endpoint: string, body: any, authState: AuthState) => baseAxiosInstance.put(getUrl(endpoint), body)

export const del = (endpoint: string, authState: AuthState) => baseAxiosInstance.delete(getUrl(endpoint))

export const sleep = (length: number) => new Promise((resolve) => setTimeout(() => resolve(undefined), length))
